import React, { useEffect } from 'react';
import {
  AppBar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogProps,
  IconButton,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import moment from 'moment';
import { EColors } from '../../../constants';
import { useGetPartnerRatingNpsDetail } from '../../../hooks';

interface IProps extends Omit<DialogProps, 'onClose'> {
  realEstateGuid: string;
  realEstateName: string;
  dateAt: string;
  onClose(): void;
}

const NPS_GROUPS = [
  { key: 'promoter', label: 'Промоутеры', bgColor: EColors.GREEN, barColor: 'success.main' },
  { key: 'neutral', label: 'Нейтралы', bgColor: EColors.YELLOW, barColor: 'warning.main' },
  { key: 'detractor', label: 'Детракторы', bgColor: EColors.RED, barColor: 'error.main' },
] as const;

const STATUS_BOX_MIN_HEIGHT = 200;

const PartnerRatingNpsDetailDialog: React.FC<IProps> = ({
  open,
  realEstateGuid,
  realEstateName,
  dateAt,
  onClose,
  ...props
}) => {
  const { fetchPartnerRatingNpsDetail, data: npsByKey, loading, error } = useGetPartnerRatingNpsDetail();

  const mapKey = `${realEstateGuid}-${dateAt}`;
  const detail = npsByKey.get(mapKey);

  useEffect(() => {
    if (!open) return;
    fetchPartnerRatingNpsDetail(realEstateGuid, dateAt);
  }, [open, realEstateGuid, dateAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderStatusBox = (content: React.ReactNode) => (
    <Box
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}
      style={{ minHeight: STATUS_BOX_MIN_HEIGHT }}
    >
      {content}
    </Box>
  );

  const groupValues = {
    promoter: { count: detail?.promoterCount, percent: detail?.promoterPercent },
    neutral: { count: detail?.neutralCount, percent: detail?.neutralPercent },
    detractor: { count: detail?.detractorCount, percent: detail?.detractorPercent },
  };

  const renderContent = () => {
    if (Boolean(error)) {
      return renderStatusBox(
        <Typography component="span" variant="h5" align="center">
          {error}
        </Typography>,
      );
    }

    if (loading || detail === undefined) return renderStatusBox(<CircularProgress />);

    if (detail === null) {
      return renderStatusBox(
        <Typography component="span" variant="h5" align="center">
          Данные отсутствуют
        </Typography>,
      );
    }

    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
          <Typography component="span" variant="h6">
            % NPS
          </Typography>
          <Typography component="span" variant="h5" style={{ color: detail.color || undefined }}>
            <b>{detail.nps}</b>
          </Typography>
        </Box>

        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, mb: 2 }}
          style={{ border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 4 }}
        >
          <Typography component="span" variant="body1">
            Всего ответов
          </Typography>
          <Typography component="span" variant="h6">
            <b>{detail.responseCount}</b>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', mb: 2 }} style={{ height: 8, borderRadius: 4, overflow: 'hidden' }}>
          {NPS_GROUPS.map((group) => (
            <Box
              key={group.key}
              sx={{ bgcolor: group.barColor }}
              style={{ width: `${groupValues[group.key].percent}%` }}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gridGap: 12 }}>
          {NPS_GROUPS.map((group) => (
            <Box
              key={group.key}
              sx={{ flex: 1, p: 1.5, textAlign: 'center' }}
              style={{ backgroundColor: group.bgColor, borderRadius: 4 }}
            >
              <Typography component="p" variant="body2" color="textSecondary">
                {group.label}
              </Typography>
              <Typography component="p" variant="h5">
                <b>{groupValues[group.key].count}</b>
              </Typography>
              <Typography component="p" variant="body2" color="textSecondary">
                {groupValues[group.key].percent}%
              </Typography>
            </Box>
          ))}
        </Box>

        {Boolean(detail.comment) && (
          <Box sx={{ mt: 2 }}>
            <Typography component="h4" variant="subtitle2" gutterBottom>
              Комментарии жителей
            </Typography>
            <Box sx={{ p: 1.5 }} style={{ border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 4 }}>
              <Typography component="span" variant="body2">
                {detail.comment}
              </Typography>
            </Box>
          </Box>
        )}
      </>
    );
  };

  return (
    <Dialog {...props} open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <AppBar style={{ position: 'relative' }}>
        <Box component={Toolbar} sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Typography component="h3" variant="h6">
              <b>Детализация NPS за {moment(dateAt, 'YYYY-MM').format('MMMM YYYY')}</b>
            </Typography>
            <Typography component="p" variant="body2">
              {realEstateName}
            </Typography>
          </Box>

          <IconButton aria-label="Закрыть" color="inherit" onClick={onClose}>
            <Close color="inherit" />
          </IconButton>
        </Box>
      </AppBar>

      <DialogContent>{renderContent()}</DialogContent>
    </Dialog>
  );
};

export default PartnerRatingNpsDetailDialog;
